import {
  Directive,
  ElementRef,
  NgZone,
  OnDestroy,
  booleanAttribute,
  inject,
  input,
  output,
} from '@angular/core';
import { overlayNavDragTranslateX, shouldDismissOverlayNav } from './overlay-nav-dismiss.utils';

/**
 * Gesto nativo: deslizar el panel overlay hacia el borde para cerrarlo.
 * El tap fuera lo resuelve el shell (backdrop + pointerdown).
 */
@Directive({
  selector: '[milincoOverlayNavDismiss]',
  standalone: true,
})
export class OverlayNavDismissDirective implements OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);

  readonly enabled = input(false, {
    alias: 'milincoOverlayNavDismiss',
    transform: booleanAttribute,
  });
  readonly rtl = input(false, { transform: booleanAttribute });
  readonly overlayNavDismissed = output<void>();

  private pointerId: number | null = null;
  private startX = 0;
  private startY = 0;
  private committed = false;
  private dismissTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly onDown = (event: PointerEvent) => this.handleDown(event);
  private readonly onMove = (event: PointerEvent) => this.handleMove(event);
  private readonly onUp = (event: PointerEvent) => this.handleUp(event);

  constructor() {
    this.zone.runOutsideAngular(() => {
      this.host.nativeElement.addEventListener('pointerdown', this.onDown, { passive: true });
    });
  }

  ngOnDestroy(): void {
    this.clearDismissTimer();
    this.host.nativeElement.removeEventListener('pointerdown', this.onDown);
    this.detachWindowListeners();
    this.clearTranslate(false);
  }

  private handleDown(event: PointerEvent): void {
    if (!this.enabled()) {
      return;
    }
    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.committed = false;
    this.attachWindowListeners();
  }

  private handleMove(event: PointerEvent): void {
    if (event.pointerId !== this.pointerId) {
      return;
    }
    const dx = event.clientX - this.startX;
    const dy = event.clientY - this.startY;
    if (!this.committed) {
      if (Math.abs(dx) < 12) {
        return;
      }
      if (Math.abs(dx) < Math.abs(dy) * 1.15) {
        this.cancelTracking();
        return;
      }
      this.committed = true;
      this.host.nativeElement.setPointerCapture?.(event.pointerId);
    }
    event.preventDefault();
    this.applyTranslate(overlayNavDragTranslateX(dx, this.rtl()));
  }

  private handleUp(event: PointerEvent): void {
    if (event.pointerId !== this.pointerId) {
      return;
    }
    const dx = event.clientX - this.startX;
    const dy = event.clientY - this.startY;
    const dismiss = this.committed && shouldDismissOverlayNav(dx, dy, this.rtl());
    this.pointerId = null;
    this.committed = false;
    this.detachWindowListeners();
    if (dismiss) {
      this.slideOffThenDismiss();
      return;
    }
    this.clearTranslate(true);
  }

  private applyTranslate(x: number): void {
    const el = this.host.nativeElement;
    el.style.transition = 'none';
    el.style.transform = `translate3d(${x}px, 0, 0)`;
  }

  private slideOffThenDismiss(): void {
    const el = this.host.nativeElement;
    const width = Math.max(el.getBoundingClientRect().width, 1);
    const off = this.rtl() ? width : -width;
    el.style.transition = 'transform 160ms ease-out';
    el.style.transform = `translate3d(${off}px, 0, 0)`;
    this.zone.run(() => this.overlayNavDismissed.emit());
    this.clearDismissTimer();
    this.dismissTimer = setTimeout(() => {
      this.dismissTimer = null;
      this.clearTranslate(false);
    }, 180);
  }

  private clearDismissTimer(): void {
    if (this.dismissTimer != null) {
      clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
  }

  private clearTranslate(animate: boolean): void {
    const el = this.host.nativeElement;
    el.style.transition = animate ? 'transform 180ms ease-out' : '';
    el.style.transform = '';
  }

  private cancelTracking(): void {
    this.pointerId = null;
    this.committed = false;
    this.detachWindowListeners();
    this.clearTranslate(true);
  }

  private attachWindowListeners(): void {
    this.detachWindowListeners();
    window.addEventListener('pointermove', this.onMove, { passive: false });
    window.addEventListener('pointerup', this.onUp, { passive: true });
    window.addEventListener('pointercancel', this.onUp, { passive: true });
  }

  private detachWindowListeners(): void {
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    window.removeEventListener('pointercancel', this.onUp);
  }
}
